import { Component, EventEmitter, Input, Output, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MindCatalogService } from '../../../core/services/mind-catalog.service';
import { ViaCEPService } from '../../../core/services/viacep.service';
import { CustomValidators } from '../../validators/custom-validators';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { ToastService } from '../../services/toast.service';
import { I18nService } from '../../../core/services/i18n.service';
import { CustomInputComponent } from '../custom-input/custom-input.component';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import { UserFacade } from '../../../core/store/user/user.facade';
import { CnpjLookupService } from '../../../core/services/cnpj-lookup.service';
import { ChannelConfigService } from '../../../core/services/channel-config.service';
import { unformatCnpj } from '../../../core/utils/cnpj.util';

export interface LocationData {
  cnpj: string;
  cep: string;
  addressNumber: string;
  city?: string;
  state?: string;
  logradouro?: string;
  neighborhood?: string;
  skipMindCatalog?: boolean;
}

@Component({
  selector: 'umkt-location-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule, I18nPipe, CustomInputComponent],
  templateUrl: './location-modal.component.html',
  styleUrl: './location-modal.component.scss'
})
export class LocationModalComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  /** Quando true, não permite fechar o modal até o usuário confirmar o endereço */
  @Input() mandatory = false;
  @Output() modalClose = new EventEmitter<void>();
  @Output() locationConfirm = new EventEmitter<LocationData>();

  private fb = inject(FormBuilder);
  private mindCatalogService = inject(MindCatalogService);
  private viaCEPService = inject(ViaCEPService);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private cartFacade = inject(CartFacade);
  private userFacade = inject(UserFacade);
  private cnpjLookupService = inject(CnpjLookupService);
  private channelConfigService = inject(ChannelConfigService);

  private destroy$ = new Subject<void>();

  get addressModalSubheadline(): string | null {
    return this.channelConfigService.addressModalSubheadline;
  }

  locationForm: FormGroup;
  isConfirming = false;
  showCnpjField = false;
  loggedInCnpj = '';

  constructor() {
    this.locationForm = this.fb.group({
      cnpj: ['', [Validators.required, CustomValidators.cnpj]],
      cep: ['', [Validators.required, CustomValidators.cep]],
      addressNumber: ['', [Validators.required, CustomValidators.addressNumber]]
    });

    const cnpjCtrl = this.locationForm.get('cnpj');
    cnpjCtrl?.valueChanges.subscribe(cnpj => {
      const raw = unformatCnpj(cnpj);
      const cleanCNPJ = raw.slice(0, 14);
      const needsPatch =
        raw !== cleanCNPJ || /[^A-Za-z0-9.\-/]/.test(cnpj || '');
      if (needsPatch) {
        cnpjCtrl?.patchValue(cleanCNPJ, { emitEvent: false });
      }
      if (cnpjCtrl?.errors?.['cnpjNotFound']) {
        const { cnpjNotFound, ...rest } = (cnpjCtrl.errors || {}) as Record<string, unknown>;
        cnpjCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
    });

    this.locationForm.get('addressNumber')?.valueChanges.subscribe(val => {
      if (val && typeof val === 'string') {
        const filtered = val.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
        if (filtered !== val) {
          this.locationForm.patchValue({ addressNumber: filtered }, { emitEvent: false });
        }
      }
    });

    const cepCtrl = this.locationForm.get('cep');
    cepCtrl?.valueChanges.subscribe(cep => {
      const raw = (cep || '').replace(/\D/g, '');
      const cleanCEP = raw.slice(0, 8);
      const needsPatch = raw !== cleanCEP || /[^0-9-]/.test(cep || '');
      if (needsPatch) {
        cepCtrl?.patchValue(cleanCEP, { emitEvent: false });
      }
      if (cepCtrl?.errors?.['cepNotFound']) {
        const { cepNotFound, ...rest } = (cepCtrl.errors || {}) as Record<string, unknown>;
        cepCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.userFacade.getIsAuthenticated(),
      this.userFacade.getUserDetails()
    ]).pipe(takeUntil(this.destroy$)).subscribe(([isAuthenticated, user]) => {
      this.showCnpjField = !isAuthenticated;
      this.loggedInCnpj = unformatCnpj(user?.document ?? '');
      this.updateCnpjValidators();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateCnpjValidators(): void {
    const cnpjCtrl = this.locationForm.get('cnpj');
    if (!cnpjCtrl) return;
    if (this.showCnpjField) {
      cnpjCtrl.setValidators([Validators.required, CustomValidators.cnpj]);
      cnpjCtrl.setValue('', { emitEvent: false });
    } else {
      cnpjCtrl.clearValidators();
      cnpjCtrl.setValue(this.loggedInCnpj, { emitEvent: false });
    }
    cnpjCtrl.updateValueAndValidity();
  }

  get cnpjControl() {
    return this.locationForm.get('cnpj');
  }

  get cnpjDigitCount(): number {
    const v = this.locationForm.get('cnpj')?.value;
    return (v || '').replace(/\D/g, '').length;
  }

  clearCnpj(): void {
    this.locationForm.get('cnpj')?.setValue('', { emitEvent: true });
  }

  get cepControl() {
    return this.locationForm.get('cep');
  }

  get cepDigitCount(): number {
    const v = this.locationForm.get('cep')?.value;
    return (v || '').replace(/\D/g, '').length;
  }

  clearCep(): void {
    this.locationForm.get('cep')?.setValue('', { emitEvent: true });
  }

  get addressNumberControl() {
    return this.locationForm.get('addressNumber');
  }

  get showAddressNumberError(): boolean {
    const ctrl = this.addressNumberControl;
    const value = (ctrl?.value ?? '').toString().trim();
    return !!(value && (ctrl?.invalid ?? false) && (ctrl?.touched || ctrl?.dirty));
  }

  clearAddressNumber(): void {
    this.locationForm.get('addressNumber')?.setValue('', { emitEvent: true });
  }

  closeModal(): void {
    if (this.mandatory) return;
    this.modalClose.emit();
  }

  private finishLocationConfirm(data: LocationData): void {
    this.isConfirming = false;
    this.toastService.show(
      this.i18nService.instant('components.header.locationModal.cepChangeProductsWarning'),
      'success',
      5000,
      'top-center'
    );
    this.cartFacade.clearCart();
    this.locationConfirm.emit(data);
    this.closeModal();
  }

  onOverlayClick(): void {
    if (this.mandatory) return;
    this.modalClose.emit();
  }

  onContentClick(event: Event): void {
    event.stopPropagation();
  }

  onConfirm(): void {
    this.locationForm.markAllAsTouched();
    if (this.locationForm.invalid || this.isConfirming) return;
    if (!this.showCnpjField && !this.loggedInCnpj) return;

    const cepCtrl = this.locationForm.get('cep');
    const { cnpj, cep, addressNumber } = this.locationForm.value;
    const cleanCEP = cep?.replace(/\D/g, '') ?? '';
    const cleanCNPJ = this.showCnpjField ? unformatCnpj(cnpj) : this.loggedInCnpj;
    const data: LocationData = {
      cnpj: cleanCNPJ,
      cep: cleanCEP,
      addressNumber: addressNumber ?? ''
    };

    this.isConfirming = true;
    const proceedToCep = (): void => {
      this.mindCatalogService.getCatalog(cleanCEP, data.addressNumber).subscribe({
        next: (resp) => {
          const ctx = resp?.context;
          data.city = ctx?.city ?? '';
          data.state = ctx?.state ?? '';
          this.viaCEPService.buscarCEP(cleanCEP).subscribe({
            next: (viaResp) => {
              if (!viaResp.erro) {
                data.logradouro = viaResp.logradouro ?? '';
                data.neighborhood = viaResp.bairro ?? '';
              }
              this.finishLocationConfirm(data);
            },
            error: () => this.finishLocationConfirm(data)
          });
        },
        error: (err: HttpErrorResponse) => {
          this.isConfirming = false;
          const status = err?.status;
          if (status === 404) {
            cepCtrl?.setErrors({ ...(cepCtrl.errors || {}), cepNotFound: true });
            return;
          }
          if (status === 500) {
            data.skipMindCatalog = true;
            this.locationConfirm.emit(data);
            this.closeModal();
            return;
          }
          cepCtrl?.setErrors({ ...(cepCtrl.errors || {}), cepNotFound: true });
        }
      });
    };
    if (this.showCnpjField && cleanCNPJ.length === 14) {
      this.cnpjLookupService.fetchOffice(cleanCNPJ).subscribe({
        next: () => proceedToCep(),
        // Soft-fail: permite confirmar mesmo se CNPJa + BrasilAPI ambos falharem (modal obrigatório UTM).
        error: () => proceedToCep(),
      });
    } else {
      proceedToCep();
    }
  }
}
